// Gravatar URL + initials helpers — port of the Workbench / Badge / Jyo
// implementation so all four apps look the same.
//
// Gravatar moved to SHA-256 in 2023; we use Web Crypto's subtle.digest so
// there's no extra dep. The `?d=mp` (mystery person) query keeps a sensible
// fallback when the user has no Gravatar profile.
//
// Post-#117: the public events API ships `gravatarHash` on speaker DTOs so
// the client never sees the raw email. Prefer `useGravatarUrlFromHash` and
// fall back to `useGravatarUrl` only on legacy surfaces (admin views) that
// still receive plaintext addresses.

import { useEffect, useMemo, useState } from 'react'

// Pure helper — given a hex SHA-256, build a sized Gravatar avatar URL.
// Returns null when the hash is missing so callers can fall back to initials.
export function gravatarUrlFromHash(hash, size = 80) {
  if (!hash) return null
  return `https://www.gravatar.com/avatar/${hash}?d=mp&s=${size}`
}

// Hook flavor for components that already hold the precomputed hash. No
// async crypto needed — keeps the render synchronous.
export function useGravatarUrlFromHash(hash, size = 80) {
  return useMemo(() => gravatarUrlFromHash(hash, size), [hash, size])
}

export function useGravatarUrl(email, size = 80) {
  const [hash, setHash] = useState(null)
  const normalized = useMemo(() => (email || '').trim().toLowerCase(), [email])

  useEffect(() => {
    if (!normalized || typeof crypto === 'undefined' || !crypto.subtle) {
      setHash(null)
      return
    }
    const encoder = new TextEncoder()
    crypto.subtle.digest('SHA-256', encoder.encode(normalized))
      .then((buf) => {
        const arr = Array.from(new Uint8Array(buf))
        setHash(arr.map((b) => b.toString(16).padStart(2, '0')).join(''))
      })
      .catch(() => setHash(null))
  }, [normalized])

  return gravatarUrlFromHash(hash, size)
}

// First-letter initials from a name pair, with email fallback. Always returns
// something printable — "?" when we have nothing at all.
export function getInitials(email, firstName, lastName) {
  if (firstName && lastName) return (firstName[0] + lastName[0]).toUpperCase()
  if (firstName) return firstName[0].toUpperCase()
  if (lastName) return lastName[0].toUpperCase()
  if (!email) return '?'
  const local = String(email).split('@')[0] || ''
  const parts = local.split(/[.\-_]/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return (local[0] || '?').toUpperCase()
}

// Convenience: split a "First Last" displayName into first/last for getInitials.
// Falls back gracefully on single-word names (uses the first char twice? no —
// just one initial).
export function initialsFromDisplayName(displayName, email) {
  if (!displayName) return getInitials(email)
  const parts = String(displayName).trim().split(/\s+/)
  if (parts.length >= 2) return getInitials(email, parts[0], parts[parts.length - 1])
  return getInitials(email, parts[0], null)
}
